import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import EmptyState from '../common/EmptyState';
import { CARD_ICON, PLUS } from '../icons';
import { buildMemberCardOptions } from '../cards/CardVisuals';
import { newItemPath } from '../items/FavoriteCardButtons';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { getCollectionMemberCardIds } from '../../utils/collections';
import { subCollectionsOf } from '../../utils/collectionFamily';
import { isBorrowedCard } from '../../utils/linkedRecords';
import { canAddItemsNow, canManage, canWriteCard } from '../../utils/permissions';
import { WATCH_HOW_VIDEOS } from '../../config/watchHowVideos';
import { newCardPath } from '../../config/collectionShell';
import { IMPORT_LABEL, importPath } from '../../utils/importEntry';

// The copy lives here once, so the items list and the collection's home can
// never drift apart. Same tone as NO_CARDS_COPY (KPR-322); Zach approves the
// wording in review (KPE-009).
export const NO_ITEMS_COPY = {
	writerTitle: 'Add your first item',
	readerTitle: 'Nothing here yet',
	whatAnItemIs: 'Each item is one record of a card, like a book or a vehicle inspection.',
	managerNext: 'Need another kind of record? Create a card, or browse ready-made ones in Discover.',
	readerNote: 'Nothing has been added yet.',
};

/**
 * The card an *Add item* can open straight onto: the collection's one
 * writable card. Counted the way the Add item picker counts (members the
 * caller may write, never a card a linked collection lends), and never when
 * sub-collections hold cards of their own — those are further choices the
 * picker groups, so a parent never guesses. null = let the person choose.
 */
export function onlyWritableCardId(collection, cards) {
	if (!collection || subCollectionsOf(collection).length) return null;
	const memberIds = getCollectionMemberCardIds(collection, cards || []);
	const options = buildMemberCardOptions(cards || [], memberIds)
		.filter(opt => !isBorrowedCard(opt.card))
		.filter(opt => canWriteCard(collection, opt.value));
	return options.length === 1 ? options[0].value : null;
}

/**
 * A collection that has cards but nothing in it, where its items would be
 * (KPR-323). The caller decides WHEN — truly empty, nothing filtering, and
 * the collection has a card (the no-cards state owns the other case) — and
 * this decides WHAT, by whether the person can add items.
 *
 * Add item opens the new-item form when there is only one card to add, and
 * otherwise clicks the page's own Add item trigger so the person gets the
 * same card chooser every other door gives them. Import a spreadsheet is the
 * other way to fill it (KPR-1899), for anyone who can add. Creating a card is
 * a further door, only for a manager; Discover is named in the copy, because
 * two secondaries is the most a surface carries.
 */
export default function NoItemsEmptyState({ collection, collectionId, className = '' }) {
	const navigate = useNavigate();
	const { cards } = useCardDefinitions();
	const canAdd = Boolean(collectionId) && canAddItemsNow(collection);
	const manager = canManage(collection) && Boolean(collectionId);
	const soleCardId = useMemo(() => onlyWritableCardId(collection, cards), [collection, cards]);

	if (!canAdd) {
		return (
			<EmptyState
				className={className}
				data-testid="no-items-empty-state"
				icon={<CARD_ICON />}
				title={NO_ITEMS_COPY.readerTitle}
				note={NO_ITEMS_COPY.readerNote}
			/>
		);
	}
	const addItem = () => {
		if (soleCardId) navigate(newItemPath(collectionId, soleCardId));
		else document.querySelector('[data-action="add-item"]')?.click();
	};
	return (
		<EmptyState
			className={className}
			data-testid="no-items-empty-state"
			icon={<CARD_ICON />}
			title={NO_ITEMS_COPY.writerTitle}
			description={manager ? [NO_ITEMS_COPY.whatAnItemIs, NO_ITEMS_COPY.managerNext] : NO_ITEMS_COPY.whatAnItemIs}
			primaryAction={{ label: 'Add item', icon: <PLUS />, onClick: addItem }}
			secondaryAction={[
				{ label: IMPORT_LABEL, to: importPath(collectionId, soleCardId) },
				manager ? { label: 'Create a card', to: newCardPath(collectionId) } : null,
			]}
			learnMore="items/add-an-item"
			video={WATCH_HOW_VIDEOS.noItems}
		/>
	);
}
